import { useState } from 'react'
import { apiPost } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'

/**
 * Hook for incidence actions (create, finish, modify).
 * @param {Function} refreshCallback Callback to refresh data after action.
 * @returns {{
 *   createIncidence: Function,
 *   finishIncidence: Function,
 *   updateIncidence: Function,
 *   saving: boolean,
 *   error: Error|null,
 *   setError: Function
 * }} Action functions and loading states.
 */
export const useIncidenceActions = (refreshCallback) => {
  const { device, worker } = useMosInfo()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Runs an incidence write action with consistent loading/error handling.
   * @param {string} actionLabel Used for console logging.
   * @param {Function} actionFn Async function that performs the API call.
   * @returns {Promise<boolean>} True when the action completes successfully.
   */
  const runAction = async (actionLabel, actionFn) => {
    setSaving(true)
    setError(null)
    try {
      await actionFn()
      if (refreshCallback) await refreshCallback()
      return true
    } catch (e) {
      console.error(`Error ${actionLabel}:`, e)
      setError(e)
      return false
    } finally {
      setSaving(false)
    }
  }

  /**
   * Creates an incidence for the current device.
   * @param {object} selectedIncidence Selected incidence definition.
   * @param {string} comment Optional operator comment.
   * @returns {Promise<boolean>} True when the incidence is created.
   */
  const createIncidence = async (selectedIncidence, comment) => {
    if (!device?.id || !selectedIncidence) return false
    return await runAction('creating incidence', async () => {
      const payload = {
        incidence: selectedIncidence,
        startDate: new Date(),
        endDate: null,
        comment: comment ?? '',
        device: device?.name ?? null,
        status: device?.status ?? null,
        createdBy: worker ?? null
      }
      await apiPost('mos/create/incidence/' + device.id, payload)
    })
  }

  /**
   * Finishes an incidence (marks it as done) for the current device.
   * @param {object} incidence Active incidence instance.
   * @returns {Promise<boolean>} True when the incidence is finished.
   */
  const finishIncidence = async (incidence) => {
    if (!device?.id || !incidence?.id) return false
    return await runAction('finishing incidence', async () => {
      const payload = {
        ...incidence,
        finishedBy: worker ?? null
      }
      await apiPost(`mos/update/incidenceAsDone/${device.id}`, payload)
    })
  }

  /**
   * Updates an incidence with a new comment.
   * @param {object} incidence Active incidence instance.
   * @param {string} comment Updated operator comment.
   * @returns {Promise<boolean>} True when the incidence is updated.
   */
  const updateIncidence = async (incidence, comment) => {
    if (!device?.id || !incidence?.id) return false
    return await runAction('updating incidence', async () => {
      const payload = {
        ...incidence,
        comment: comment ?? ''
      }
      await apiPost(`mos/update/${device.id}`, payload)
    })
  }

  return {
    createIncidence,
    finishIncidence,
    updateIncidence,
    saving,
    error,
    setError // To clear errors manually
  }
}
